export interface HelpSection {
  heading: string
  text: string
}

export interface HelpPage {
  title: string
  intro?: string
  sections: HelpSection[]
}

export const HELP_CONTENT: Record<string, HelpPage> = {
  '/dashboard': {
    title: 'Главный экран',
    intro: 'Обзор всех проектов организации в одном месте.',
    sections: [
      {
        heading: 'KPI-плашки (верхний блок)',
        text: 'Три счётчика слева направо: просрочено разделов (красный) - разделы с истёкшим сроком сдачи; срок через 5 дней (жёлтый) - разделы требующие внимания; принято за месяц (зелёный) - разделы в статусе «Принят» за последние 30 дней.',
      },
      {
        heading: 'Диаграмма Ганта',
        text: 'Горизонтальная лента проектов «В работе». Красный бар - в проекте есть просрочка. Жёлтый - срок менее 5 дней. Серый - нет просрочек. Нажмите на бар чтобы открыть карточку проекта.',
      },
      {
        heading: 'Три зоны',
        text: 'Слева - Предпроект (подготовка и договор). По центру - В работе (активное проектирование). Справа - Завершённые. Цифра в заголовке зоны = количество проектов.',
      },
      {
        heading: 'Блок «Требуют внимания»',
        text: 'Разделы с просрочкой или приближающимся сроком. Переключайте фильтры: Все / Просрочено / Скоро срок. Нажмите на раздел → откроется карточка проекта.',
      },
    ],
  },

  '/projects': {
    title: 'Список проектов',
    sections: [
      {
        heading: 'Вкладки',
        text: 'Активные - проекты в стадии Предпроект и В работе. Завершённые - постпроектная стадия. Удалённые - корзина (только директора и замдиры).',
      },
      {
        heading: 'Цвет статуса',
        text: 'Зелёный - всё в порядке. Жёлтый - срок менее 5 дней. Красный - есть просрочка. Серый - архив или завершён.',
      },
      {
        heading: 'Корзина',
        text: 'Удалённые проекты хранятся в корзине. Восстановите проект или удалите навсегда. Все данные разделов сохраняются при мягком удалении.',
      },
    ],
  },

  '/projects/new': {
    title: 'Создание проекта',
    sections: [
      { heading: 'Шифр проекта', text: 'Уникальный идентификатор, например «ГГП-2024-001». Шифр нельзя изменить после создания.' },
      { heading: 'Название', text: 'Полное наименование объекта проектирования.' },
      { heading: 'Заказчик', text: 'Наименование заказчика или организации.' },
      { heading: 'Адрес', text: 'Адрес объекта строительства/реконструкции.' },
      { heading: 'Даты', text: 'Начало договора и плановая дата сдачи. Только директор и замдиректора могут изменить даты после создания.' },
      { heading: 'ГИП', text: 'Главный инженер проекта - ответственный за ведение документации. Получит уведомление по почте при назначении.' },
      { heading: 'Договорная стоимость', text: 'Необязательное поле. Видно только директору и замдиру. Укажите общую стоимость договора.' },
    ],
  },

  '/projects/:id': {
    title: 'Карточка проекта',
    sections: [
      { heading: 'Вкладки ИРД / ПД / ГГЭ / РД', text: 'Четыре типа документации: ИРД - исходно-разрешительная (включая инженерные изыскания), ПД - проектная (состав по ПП-87), ГГЭ - государственная экспертиза, РД - рабочая документация.' },
      { heading: 'Разделы', text: 'Каждый раздел - это отдельный документ или часть проекта. Укажите шифр (например «АС», «КЖ», «ОВ»), назначьте исполнителя и установите сроки.' },
      { heading: 'Этапы раздела', text: 'Нажмите ▶ Этапы рядом с шифром раздела чтобы развернуть подэтапы. Каждый этап отображается отдельным баром на диаграмме Ганта.' },
      { heading: 'Статусы', text: 'Не начат → В работе → На проверке → Выдан → Принят. Статус «Принят» засчитывается в KPI-счётчик главного экрана.' },
      { heading: 'Стоимость раздела', text: 'Видна только директору и замдиру. Укажите договорную стоимость этапа документации.' },
    ],
  },

  '/users': {
    title: 'Управление сотрудниками',
    sections: [
      { heading: 'Роли', text: 'Директор/Администратор - полный доступ и стоимости. Зам. директора - те же права. ГИП (главный инженер) - управляет своими проектами и разделами. Проектировщик - видит только свои разделы.' },
      { heading: 'Приглашение', text: 'Укажите email сотрудника и роль. Придёт письмо со ссылкой для установки пароля. ФИО и телефон сотрудник заполнит сам.' },
      { heading: 'Повторное приглашение', text: 'Кнопка «Пригласить» рядом с сотрудником без первого входа - отправляет новую ссылку.' },
      { heading: 'Блокировка', text: 'Кнопка питания отключает доступ сотруднику без удаления данных. Можно восстановить в любой момент.' },
    ],
  },

  '/audit': {
    title: 'Журнал действий',
    sections: [
      { heading: 'Категории', text: 'Входы - кто и когда вошёл в систему. Проекты - создание, изменение, удаление проектов. Разделы - смена статусов и исполнителей. Сотрудники - управление аккаунтами.' },
      { heading: 'Фильтры', text: 'Выберите категорию (кнопки-чипы) или конкретного сотрудника для фильтрации журнала.' },
      { heading: 'IP-адрес', text: 'Показывает с какого IP было действие - помогает отследить несанкционированный доступ.' },
    ],
  },
}

/** Находит контент помощи для текущего пути (поддерживает /projects/:id) */
export function findHelpContent(pathname: string): HelpPage | null {
  if (HELP_CONTENT[pathname]) return HELP_CONTENT[pathname]
  // Матч /projects/:id (не /projects/new)
  if (/^\/projects\/[^/]+$/.test(pathname) && pathname !== '/projects/new') {
    return HELP_CONTENT['/projects/:id'] ?? null
  }
  return null
}
